import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowRightLeft,
  Info,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { useId, useState } from "react";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { BrebKeyPlate } from "@/shared/components/payments/breb-key/BrebKeyPlate";
import { BREB_KEY_SOURCE_NAME } from "@/shared/components/payments/breb-key/brebKeySources";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type {
  BrebKeyTransferPlan,
  BrebKeyTransferProblem,
  BrebKeyTransferResult,
  BrebReceivingKey,
} from "@/shared/services/payments/brebReceivingKey.service";
import { colocaToast } from "@/shared/utils/toast";

import { useBrebKeyTransfer } from "../hooks/useBrebKeyTransfer";
import type { AdminOrganization } from "../types/organizationsAdmin.types";

const TRANSFER_PROBLEM_TEXT: Record<BrebKeyTransferProblem, keyof Translations["organizations"]> = {
  verification_required: "custom_key_transfer_problem_verification_required",
  rail_disabled: "custom_key_transfer_problem_rail_disabled",
  no_pricing: "custom_key_transfer_problem_no_pricing",
};

export interface BrebKeyTransferTarget {
  /** The organization that holds the key now. */
  organization: AdminOrganization;
  key: BrebReceivingKey;
}

export interface TransferBrebKeyDialogProps {
  /** Null closes the dialog. */
  transfer: BrebKeyTransferTarget | null;
  onOpenChange: (open: boolean) => void;
}

export function TransferBrebKeyDialog({ transfer, onOpenChange }: TransferBrebKeyDialogProps) {
  if (!transfer) return null;
  return (
    <TransferBrebKeyDialogContent
      key={transfer.key.id}
      organization={transfer.organization}
      brebKey={transfer.key}
      onClose={() => onOpenChange(false)}
    />
  );
}

interface ContentProps {
  organization: AdminOrganization;
  brebKey: BrebReceivingKey;
  onClose: () => void;
}

function TransferBrebKeyDialogContent({ organization, brebKey, onClose }: ContentProps) {
  const { t } = useTranslations("organizations");
  const { t: tBatches } = useTranslations("payoutBatches");
  const pickerId = useId();
  const catalog = useOrganizationsCatalog();
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const {
    plan: planState,
    reloadPlan,
    transfer,
  } = useBrebKeyTransfer(brebKey.id, organization.id, destinationId);
  const twoFactor = useTwoFactorAction<BrebKeyTransferResult>();
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const plan = planState.status === "ready" ? planState.plan : null;
  const movable = !!plan && !plan.destination.problem && !hasOpenOtc(plan);

  const move = async () => {
    if (!plan || plan.destination.problem || hasOpenOtc(plan)) return;
    const destination = plan.destination;
    setMoving(true);
    setMoveError(null);
    try {
      const moved = await twoFactor.start((code) => transfer(destination.organization_id, code));
      if (!moved) return;
      colocaToast.success(
        t("custom_key_transferred_toast", { key: moved.key.key_value, name: destination.name }),
      );
      onClose();
    } catch (error) {
      setMoveError(HttpApiService.getErrorMessage(error));
      reloadPlan();
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <ColocaDialog
        open
        onOpenChange={(open) => {
          if (!open && !moving) onClose();
        }}
        size="lg"
        title={t("custom_key_transfer_title", { key: brebKey.key_value })}
        description={t("custom_key_transfer_description")}
        showCloseButton={!moving}
        footer={
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={moving} />}>
              {t("custom_key_cancel")}
            </ColocaDialogClose>
            <ColocaButton
              variant="primary"
              icon={ArrowRightLeft}
              loading={moving}
              disabled={!movable || moving}
              onClick={() => void move()}
            >
              {t("custom_key_transfer_submit")}
            </ColocaButton>
          </>
        }
      >
        <div className="space-y-5">
          <BrebKeyPlate keyValue={brebKey.key_value} showActions={false} />

          <OrganizationScopePicker
            id={pickerId}
            mode="required"
            value={destinationId}
            onValueChange={(organizationId) => {
              setDestinationId(organizationId);
              setMoveError(null);
            }}
            disabled={moving}
            catalog={catalog}
            labels={{
              label: t("custom_key_transfer_picker_label"),
              placeholder: tBatches("org_picker_placeholder"),
              loading: tBatches("org_picker_loading"),
              empty: tBatches("org_picker_empty"),
              searching: tBatches("org_picker_searching"),
              searchError: tBatches("org_picker_search_error"),
              catalogError: tBatches("org_picker_catalog_error"),
              catalogTruncated: tBatches("org_picker_catalog_truncated"),
              requiredHint: t("custom_key_transfer_picker_required_hint"),
              orgHint: t("custom_key_transfer_picker_org_hint"),
            }}
          />

          {destinationId === organization.id && (
            <ColocaAlert variant="warning" icon={AlertCircle} text={t("custom_key_transfer_same_org")} />
          )}

          {planState.status === "loading" && (
            <div className="space-y-3" aria-busy="true" aria-label={t("custom_key_transfer_loading")}>
              <ColocaSkeleton className="h-28 w-full rounded-2xl" />
              <ColocaSkeleton className="h-16 w-full" />
            </div>
          )}

          {planState.status === "error" && (
            <ColocaAlert
              variant="destructive"
              icon={AlertCircle}
              text={planState.message}
              actions={
                <ColocaButton size="sm" icon={RefreshCw} onClick={reloadPlan}>
                  {t("custom_key_retry")}
                </ColocaButton>
              }
            />
          )}

          {plan && <TransferPlanSummary plan={plan} originName={organization.name} />}

          {moveError && <ColocaAlert variant="destructive" icon={AlertCircle} text={moveError} />}
        </div>
      </ColocaDialog>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("custom_key_transfer_two_factor_required")}
        challengeDescription={
          plan
            ? t("custom_key_transfer_two_factor_description", {
                key: brebKey.key_value,
                name: plan.destination.name,
              })
            : undefined
        }
      />
    </>
  );
}

/** The OTC desk can still pay to the key: a sale's pesos or a purchase's refund would reach the new holder. */
function hasOpenOtc(plan: BrebKeyTransferPlan): boolean {
  return (plan.origin.open_sales ?? 0) > 0 || (plan.origin.open_purchases ?? 0) > 0;
}

function TransferPlanSummary({ plan, originName }: { plan: BrebKeyTransferPlan; originName: string }) {
  const { t } = useTranslations("organizations");
  const { t: tPayins } = useTranslations("payins");
  const keyValue = plan.key.key_value;
  const destinationName = plan.destination.name;
  const becomesPrimary = plan.destination.primary_key_value === keyValue;

  return (
    <section className="space-y-4" aria-live="polite">
      {hasOpenOtc(plan) && (
        <ColocaAlert
          variant="warning"
          icon={ShieldAlert}
          heading={t("custom_key_transfer_otc_title", { name: originName })}
          text={[
            (plan.origin.open_sales ?? 0) > 0 &&
              t("custom_key_transfer_otc_sales", { count: plan.origin.open_sales ?? 0 }),
            (plan.origin.open_purchases ?? 0) > 0 &&
              t("custom_key_transfer_otc_purchases", { count: plan.origin.open_purchases ?? 0 }),
            t("custom_key_transfer_otc_wait"),
          ]
            .filter(Boolean)
            .join(" ")}
        />
      )}

      {plan.destination.problem && (
        <ColocaAlert
          variant="warning"
          icon={ShieldAlert}
          heading={t("custom_key_transfer_problem_title", { name: destinationName })}
          text={t(TRANSFER_PROBLEM_TEXT[plan.destination.problem])}
        />
      )}

      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-stretch">
        <TransferSide
          label={t("custom_key_transfer_side_from")}
          name={originName}
          primaryKey={plan.origin.primary_key_value}
          tag={
            plan.key.is_primary
              ? t("custom_key_transfer_primary_changes")
              : t("custom_key_transfer_primary_same")
          }
          tagVariant={plan.key.is_primary ? "warning" : "secondary"}
        />
        <div className="flex items-center justify-center text-muted-foreground" aria-hidden="true">
          <ArrowDown className="size-4 sm:hidden" />
          <ArrowRight className="hidden size-4 sm:block" />
        </div>
        <TransferSide
          label={t("custom_key_transfer_side_to")}
          name={destinationName}
          primaryKey={plan.destination.primary_key_value}
          tag={becomesPrimary ? t("custom_key_transfer_primary_this") : t("custom_key_transfer_primary_same")}
          tagVariant={becomesPrimary ? "success" : "secondary"}
        />
      </div>

      <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground marker:text-border">
        <li>
          {t("custom_key_transfer_arrives_as", {
            name: destinationName,
            source: tPayins(BREB_KEY_SOURCE_NAME[plan.arrives_as]).toLocaleLowerCase(),
          })}
        </li>
        <li>{t("custom_key_transfer_static_qr", { name: destinationName })}</li>
        <li>{t("custom_key_transfer_in_flight")}</li>
      </ul>

      {plan.live_qrs > 0 && (
        <ColocaAlert
          variant="secondary"
          icon={Info}
          text={t("custom_key_transfer_live_qrs", { name: originName, count: plan.live_qrs })}
        />
      )}
    </section>
  );
}

interface TransferSideProps {
  label: string;
  name: string;
  /** Null: Coloca's shared key. */
  primaryKey: string | null;
  tag: string;
  tagVariant: "secondary" | "success" | "warning";
}

function TransferSide({ label, name, primaryKey, tag, tagVariant }: TransferSideProps) {
  const { t } = useTranslations("organizations");
  return (
    <div className="min-w-0 rounded-2xl border border-border p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate text-sm font-semibold text-foreground">{name}</p>
      <p className="mt-3 text-xs text-muted-foreground">{t("custom_key_transfer_primary")}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "min-w-0 truncate text-sm",
            primaryKey ? "font-mono font-semibold text-foreground" : "text-muted-foreground",
          )}
        >
          {primaryKey ?? t("custom_key_transfer_primary_shared")}
        </span>
        <ColocaBadge variant={tagVariant}>{tag}</ColocaBadge>
      </div>
    </div>
  );
}
